
import { Vue, Component, Provide } from "vue-property-decorator";
import ClassProperties from "@/oxygen/classes/ClassProperties.vue";
import ControlsTree from "@/components/Reusable/ControlsTree/ControlsTree.vue";
import { configStore, documentStore, uiStore } from "@/store/store";
import Icon from "@/components/UI/Icon.vue";
import { editingElementControlsTreeContext } from "@/lib/components/controls";
import { filterControlsThatCanBeModified } from "@/lib/permissions/utils";
import ClassDropdown from "@/oxygen/classes/ClassDropdown.vue";
import { IconName } from "@/lib/icons/icons";
import OxygenHtmlAttributes from "@/oxygen/properties-panel/OxygenHtmlAttributes.vue";
import PanelMask from "@/oxygen/PanelMask.vue";
import { COMPONENT_SLUG } from "@/lib/elements/constants";
import OxygenCopyPastContextMenu from "@/oxygen/properties-panel/OxygenCopyPastContextMenu.vue";
import defensiveCopy from "@/util/defensive-copy";
import { isObject } from "@/util/objects";
import { doesControlHaveValueSetOrDoesControlSectionHaveDescendantsWithValue } from "@/lib/controls/values";

type Tab = {
  name: string;
  slug: string;
  icon: IconName;
  hidden?: boolean;
};

@Component({
  components: {
    PanelMask,
    Icon,
    ControlsTree,
    ClassDropdown,
    ClassProperties,
    OxygenHtmlAttributes,
    OxygenCopyPastContextMenu
  }
})
export default class OxygenTabsAndControls extends Vue {
  @Provide("context")
  editingElementControlsTreeContext = editingElementControlsTreeContext;

  settingsControlsAsTabs: Tab[] = [
    {
      name: "Override Variables",
      slug: "override_variables",
      icon: "DollarSolidIcon",
      hidden: this.isComponent
    },
    {
      name: "Conditions",
      slug: "conditions",
      icon: "ConditionsSolidIcon"
    },
    {
      name: "Interactions",
      slug: "interactions",
      icon: "ArrowPointerSolidIcon",
      hidden: this.isComponent
    },
    {
      name: "Animations",
      slug: "animations",
      icon: "PlaySolidIcon",
      hidden: this.isComponent
    }
  ];

  get activeTabSlug() {
    if (this.elementId && uiStore.activeElementProperties?.tab) {
      return uiStore.activeElementProperties.tab;
    }

    if (this.controls.contentSections.length) {
      return "content";
    }

    return "classes";
  }

  get tabs() {
    const settingsTabs = this.settingsControlsAsTabs;

    return [
      {
        name: "Content",
        slug: "content",
        icon: "PenSolidIcon",
        hidden: this.controls.contentSections.length === 0
      },
      {
        name: "Design",
        slug: "design",
        icon: "PaletteSolidIcon",
        hidden: this.controls.designSections.length === 0 || this.isComponent
      },
      ...settingsTabs,
      {
        name: "Settings",
        slug: "settings",
        icon: "GearSolidIcon",
        hidden: this.controls.settingsSections.length === 0 || this.isComponent
      },
      {
        name: "Classes",
        slug: "classes",
        icon: "ClassesIcon",
        hidden: true
      },
      {
        name: "Attributes",
        slug: "attributes",
        hidden: true
      }
    ] as Tab[];
  }

  get filteredTabs() {
    return this.tabs.filter(tab => !tab?.hidden);
  }

  get activeTab() {
    return this.tabs.find(tab => tab.slug === this.activeTabSlug);
  }

  get element() {
    return uiStore.activeElement;
  }

  get elementId() {
    return uiStore.activeElementId;
  }

  get activeSelector() {
    return uiStore.activeOxySelector;
  }

  get activeNestedSelector() {
    return uiStore.activeOxyNestedSelector;
  }

  get controls() {
    // @ts-ignore
    const type = this.element.data.type;
    const controls = configStore.element(type).controls;

    // for client mode / permissions
    return filterControlsThatCanBeModified(controls, configStore.permission);
  }

  get controlSections() {
    if (this.activeTabSlug === "content") {
      return this.controls.contentSections;
    }

    if (this.activeTabSlug === "design") {
      return this.controls.designSections;
    }

    if (this.isTopLevelSettingsTab(this.activeTabSlug)) {
      return this.controls.settingsSections.filter(
        section => section.slug === this.activeTabSlug
      );
    }

    if (this.activeTabSlug === "settings") {
      return this.controls.settingsSections.filter(
        section =>
          !this.settingsControlsAsTabs.some(tab => tab.slug === section.slug)
      );
    }

    return [];
  }

  get path() {
    if (this.activeTabSlug === "content") {
      return "content";
    }

    if (this.activeTabSlug === "design") {
      return "design";
    }

    if (
      this.isTopLevelSettingsTab(this.activeTabSlug) ||
      this.activeTabSlug === "settings"
    ) {
      return "settings";
    }

    return "";
  }

  get isComponent() {
    if (!this.element) return false;
    return this.element?.data?.type === COMPONENT_SLUG;
  }

  isTopLevelSettingsTab(slug: string) {
    return this.settingsControlsAsTabs.some(tab => tab.slug === slug);
  }

  updateTab(tab?: string) {
    uiStore.setCurrentElementOxySelector(undefined);
    uiStore.setCurrentElementTab(tab);
  }

  getSettingsData(slug: string) {
    const isTopLevel = this.isTopLevelSettingsTab(slug);
    const path = isTopLevel ? `settings.${slug}` : slug;
    const value = this.editingElementControlsTreeContext.getControlValue(path);

    if (slug === "settings") {
      const copiedSettings = defensiveCopy(value) as Record<string, unknown>;

      if (!isObject(copiedSettings)) return copiedSettings;

      this.settingsControlsAsTabs.forEach(
        tab => delete copiedSettings[tab.slug]
      );

      return copiedSettings;
    }

    return value;
  }

  setSettingsData(slug: string, newData: Record<string, unknown>) {
    if (!this.elementId) return;

    const isTopLevel = this.isTopLevelSettingsTab(slug);
    const path = isTopLevel ? `settings.${slug}` : slug;

    if (slug === "settings") {
      this.updateSettingsButIgnoreTopLevelTabs(newData);
      return;
    }

    documentStore.throttledPropertyChanged({
      elementId: this.elementId,
      path,
      value: newData,
      meta: { snapshotLabel: `Paste ${slug}` }
    });
  }

  updateSettingsButIgnoreTopLevelTabs(newData: Record<string, unknown>) {
    if (!this.elementId) return;

    const currentSettings = (defensiveCopy(
      this.editingElementControlsTreeContext.getControlValue("settings")
    ) || {}) as Record<string, unknown>;

    const updatedSettings = defensiveCopy(newData) || {};

    this.settingsControlsAsTabs.forEach(tab => {
      if (isObject(currentSettings) && tab.slug in currentSettings) {
        updatedSettings[tab.slug] = currentSettings[tab.slug];
      }
    });

    documentStore.throttledPropertyChanged({
      elementId: this.elementId,
      path: "settings",
      value: updatedSettings,
      meta: { snapshotLabel: "Paste settings" }
    });
  }

  tabHasData(slug: string) {
    return doesControlHaveValueSetOrDoesControlSectionHaveDescendantsWithValue(
      this.getSettingsData(slug)
    );
  }
}
